

// src/context/WebSocketContext.js
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'auth/Auth';
import { getAccessToken } from 'auth/tokenStore';
import { getWebSocketBaseUrl } from 'utils/apiBaseUrl';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isPublicPayPage = pathname.startsWith('/pay');
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const isConnectingRef = useRef(false);
  const heartbeatIntervalRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  // A single bumped object rather than an array: this context is mounted on
  // every page, and an unbounded event list would leak for the whole session.
  const [telephonyEvent, setTelephonyEvent] = useState(null);
  const telephonySeqRef = useRef(0);
  // WhatsApp inbox/badge: bumped when Meta webhooks (or local claim/send/read)
  // publish whatsapp_inbox_updated. Same coalesced pattern as telephonyEvent so
  // we do not grow an unbounded list on every inbound message.
  const [whatsappInboxEvent, setWhatsappInboxEvent] = useState(null);
  const whatsappInboxSeqRef = useRef(0);
  // Queue stats arrive ~1Hz per queue. Coalesced through a ref and flushed on
  // an animation frame, because a setState per message re-renders every
  // consumer of this context -- which includes the notification bell on every
  // page in the app.
  const queueStatsRef = useRef({});
  const [queueStats, setQueueStats] = useState({});
  const statsFlushRef = useRef(null);
  const [agentPresence, setAgentPresence] = useState({});

  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const heartbeatInterval = 30000;
  const connectionTimeout = 5000;

  const startHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send('ping');
        } catch (error) {
          console.debug('Heartbeat failed:', error);
          handleReconnect();
        }
      }
    }, heartbeatInterval);
  };

  const clearHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const handleReconnect = () => {
    if (reconnectAttemptRef.current >= maxReconnectAttempts) {
      console.debug('Max reconnection attempts reached');
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptRef.current += 1;
      connectWebSocket();
    }, reconnectDelay * Math.min(reconnectAttemptRef.current + 1, 3)); // Exponential backoff up to 3x
  };

  const connectWebSocket = () => {
    if (!user?.id || isConnectingRef.current) return;

    // The socket is authenticated by the same access token the REST API uses.
    // Without it the server closes with 4001, so don't even open the socket.
    const accessToken = getAccessToken();
    if (!accessToken) return;
  
    try {
      // Close existing connection properly
      if (wsRef.current) {
        wsRef.current.onclose = null; // Remove existing onclose handler
        wsRef.current.close();
        wsRef.current = null;
      }
  
      isConnectingRef.current = true;
      clearHeartbeat();
  
      const schemaFromUser = typeof user?.schema_name === 'string' ? user.schema_name : null;
      const hostname = window.location.hostname;
      const hostnamePrefix = (hostname.split('.')[0] || '').toLowerCase();
      const reservedPrefixes = new Set(['localhost', '127', 'app']);
      const schemaFromHostname =
        hostnamePrefix && !reservedPrefixes.has(hostnamePrefix)
          ? `tenant_${hostnamePrefix}`
          : null;
      const schema = schemaFromUser || schemaFromHostname || 'tenant_master';
      const wsBaseUrl = getWebSocketBaseUrl();
      const wsUrl = `${wsBaseUrl}/ws/${schema}/${user.id}?token=${encodeURIComponent(accessToken)}`;
  
      const newWs = new WebSocket(wsUrl);
      wsRef.current = newWs;
  
      // Connection timeout
      const connectionTimeoutId = setTimeout(() => {
        if (newWs.readyState !== WebSocket.OPEN) {
          console.debug('Connection timeout');
          newWs.close();
          isConnectingRef.current = false;
          handleReconnect();
        }
      }, connectionTimeout);
  
      newWs.onopen = () => {
        if (wsRef.current !== newWs) {
          // Another connection has been initiated
          newWs.close();
          return;
        }
        clearTimeout(connectionTimeoutId);
        console.debug('WebSocket connected');
        setWsConnected(true);
        reconnectAttemptRef.current = 0;
        isConnectingRef.current = false;
        startHeartbeat();
      };
  
      // Add the onmessage handler
      newWs.onmessage = (event) => {
        try {
          // Handle ping/pong
          if (event.data === 'ping') {
            newWs.send('pong');
            return;
          }
  
          const data = JSON.parse(event.data);
          console.debug('WebSocket message received:', data); // Debug log
  
          switch (data.type) {
            case 'initial_notifications':
              setNotifications(prevNotifications => {
                const newNotifications = [...data.notifications];
                const existingIds = new Set(prevNotifications.map(n => n.id));
                const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n.id));
                return [...uniqueNewNotifications, ...prevNotifications];
              });
              break;
  
            case 'new_notification':
              setNotifications(prevNotifications => {
                // Prevent duplicate notifications
                if (prevNotifications.some(n => n.id === data.data.id)) {
                  return prevNotifications;
                }
                return [data.data, ...prevNotifications];
              });
              break;
  
            case 'call_ringing':
            case 'call_answered':
            case 'call_ended':
            case 'call_updated':
            case 'call_transferred':
            case 'call_context_resolved':
            case 'supervision_started':
            case 'supervision_ended':
            case 'recording_ready':
            case 'transcript_ready':
              telephonySeqRef.current += 1;
              setTelephonyEvent({ seq: telephonySeqRef.current, type: data.type, payload: data });
              break;

            case 'whatsapp_inbox_updated':
              whatsappInboxSeqRef.current += 1;
              setWhatsappInboxEvent({
                seq: whatsappInboxSeqRef.current,
                type: data.type,
                payload: data,
              });
              break;

            case 'agent_presence':
              setAgentPresence((prev) => ({
                ...prev,
                [data.agent_id]: {
                  state: data.presence,
                  since: data.since,
                  current_call_sid: data.current_call_sid,
                },
              }));
              break;

            case 'queue_stats':
            case 'queue_entry':
            case 'queue_exit':
              queueStatsRef.current = {
                ...queueStatsRef.current,
                [data.queue_id]: { ...(queueStatsRef.current[data.queue_id] || {}), ...data },
              };
              if (!statsFlushRef.current) {
                statsFlushRef.current = requestAnimationFrame(() => {
                  statsFlushRef.current = null;
                  setQueueStats(queueStatsRef.current);
                });
              }
              break;

            default:
              console.debug('Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };
  
      newWs.onclose = (event) => {
        if (wsRef.current !== newWs) return;
        
        console.debug('WebSocket closed:', event.code, event.reason);
        setWsConnected(false);
        isConnectingRef.current = false;
        clearHeartbeat();
        
        // Only reconnect if it's not an intentional close
        if (event.code !== 1000) {
          handleReconnect();
        }
      };
  
      newWs.onerror = (error) => {
        console.debug('WebSocket error:', error);
        setWsConnected(false);
        isConnectingRef.current = false;
      };
  
    } catch (error) {
      console.debug('WebSocket connection error:', error);
      isConnectingRef.current = false;
      handleReconnect();
    }
  };

  // Connect when user is available (skip on public payment links)
  useEffect(() => {
    if (user?.id && !isPublicPayPage) {
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      clearHeartbeat();
      isConnectingRef.current = false;
    };
  }, [user?.id, isPublicPayPage]);

  // The socket carries the access token in its URL, so a rotated token means the
  // existing connection is authenticated with a stale credential. Reconnect on
  // the global token event rather than waiting for the server to close us.
  useEffect(() => {
    const handleTokenUpdated = () => {
      if (!user?.id || isPublicPayPage) return;
      if (!getAccessToken()) return;
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      isConnectingRef.current = false;
      reconnectAttemptRef.current = 0;
      connectWebSocket();
    };

    window.addEventListener('auth-token-updated', handleTokenUpdated);
    return () => window.removeEventListener('auth-token-updated', handleTokenUpdated);
  }, [user?.id, isPublicPayPage]);

  // Reconnect on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id && !isPublicPayPage) {
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          console.debug('Reconnecting due to visibility change');
          connectWebSocket();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id]);

  // Handle window focus
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id && wsRef.current?.readyState !== WebSocket.OPEN) {
        console.debug('Reconnecting due to window focus');
        connectWebSocket();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  // Memoised: this context is consumed by the notification bell on every page,
  // so rebuilding the object each render re-renders the whole app.
  const value = useMemo(
    () => ({
      wsConnected,
      notifications,
      setNotifications,
      telephonyEvent,
      whatsappInboxEvent,
      agentPresence,
      queueStats,
    }),
    [wsConnected, notifications, telephonyEvent, whatsappInboxEvent, agentPresence, queueStats]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketProvider;